import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import { getDeliveryRunProducts, getDeliveryUnitProducts } from '../services/delivery-runs.service';
import type {
  ProductLeafPage,
  ProductsUnavailable,
  RunProductsQuery,
} from '../types/delivery-runs.types';

/** Page size for the product-leaf cursor pages. */
const LEAF_PAGE_SIZE = 100;
/** Refetch cadence (ms) while any loaded leaf is still awaiting a destination verdict. */
const LEAF_SYNC_POLL_MS = 15_000;

/**
 * Poll only while some loaded leaf reports `syncIsTerminal === false` — the
 * server's own sentinel — so the ledger's outcome / reason cells stay current
 * while the destination is still ruling, without hardcoding the terminal
 * sync-status set; stops the moment none are pending.
 */
function syncPollInterval(query: {
  state: { data?: InfiniteData<ProductLeafPage> | undefined };
}): number | false {
  const pages = query.state.data?.pages;
  if (!pages) return false;
  return pages.some((p) => p.products.some((leaf) => leaf.syncIsTerminal === false))
    ? LEAF_SYNC_POLL_MS
    : false;
}

/**
 * Why the run has no product rows at all, read off the FIRST page — the server sets
 * it only on a first, unfiltered, empty page of a terminal run; `null` means an empty
 * list is just "nothing matched". The service has already folded the legacy
 * `productsUnavailableReason` string into this block.
 */
export function productsUnavailableOf(
  data: InfiniteData<ProductLeafPage> | undefined,
): ProductsUnavailable | null {
  return data?.pages[0]?.productsUnavailable ?? null;
}

/** Next offset from a leaf page, or undefined at the end (`hasMore` is the only signal). */
function nextOffset(lastPage: { hasMore: boolean; offset: number; limit: number }) {
  return lastPage.hasMore ? lastPage.offset + lastPage.limit : undefined;
}

/**
 * Run-wide product-leaf ledger (offset cursor). Pages accumulate under one slot
 * keyed by the filter permutation (outcome/sku/errorCode/search/sortBy);
 * changing a filter swaps the slot and resets to the first page.
 */
export function useDeliveryRunProducts(
  runId: string,
  query: RunProductsQuery = {},
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: queryKeys.deliveries.runProducts(runId, query as Record<string, unknown>),
    queryFn: ({ pageParam }) =>
      getDeliveryRunProducts(runId, { ...query, limit: LEAF_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    enabled: !!runId && enabled,
    refetchInterval: syncPollInterval,
  });
}

/** Product leaves for a single work unit (offset cursor), same paging shape. */
export function useDeliveryUnitProducts(
  runId: string,
  unitIndex: number,
  query: RunProductsQuery = {},
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: queryKeys.deliveries.runUnitProducts(
      runId,
      unitIndex,
      query as Record<string, unknown>,
    ),
    queryFn: ({ pageParam }) =>
      getDeliveryUnitProducts(runId, unitIndex, {
        ...query,
        limit: LEAF_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: nextOffset,
    enabled: !!runId && enabled,
    refetchInterval: syncPollInterval,
  });
}
